import { useState, type ReactNode } from 'react';
import {
  Alert, Anchor, Button, Card, Divider, Group, Pagination, Select, SimpleGrid, Stack,
  Table, Text, Title
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { LineChart } from '@mantine/charts';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import type { ExpenseCategory, FinancialSummaryResponse } from 'washday-sdk';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import { useGetExpenses } from '../../../hooks/expenses/queries';
import { useGetFinancialSummary } from '../../../hooks/reports/queries';
import { useGetStoreById } from '../../../hooks/stores/queries';
import { canViewFinancialSummary } from '../../../utils/permission-utils';
import { formatMoneyString } from '../../../utils/strings';
import {
  buildFinancialDrilldownLink, buildFinancialSummaryDates, getFinancialSummaryMonth
} from './filters';

const EXPENSE_PAGE_SIZE = 10;

function ReportLink({ href, children }: { href: string; children: ReactNode }) {
  const history = useHistory();
  return <Anchor href={href} size="sm" onClick={(event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    history.push(href);
  }}>{children}</Anchor>;
}

function Metric({ id, label, value, help, href, compact = false }: {
  id: string; label: string; value: string; help?: string; href?: string; compact?: boolean
}) {
  return <Card withBorder={!compact} radius="md" padding={compact ? "xs" : "md"} data-testid={`metric-${id}`}>
    <Text c="dimmed" size="sm">{label}</Text>
    <Text fw={700} style={{ fontSize: 'clamp(1.25rem, 2.4vw, 1.8rem)', overflowWrap: 'anywhere' }}>
      {value}
    </Text>
    {help && <Text size="xs" c="dimmed" mt="xs">{help}</Text>}
    {href && <ReportLink href={href}>{label}</ReportLink>}
  </Card>;
}

function FinancialSummaryContent({ storeId, timezone }: { storeId: string; timezone: string }) {
  const { t } = useTranslation(['ReportsPage', 'ExpensesPage', 'FieldName', 'CommonButtons', 'Backend']);
  const text = (key: string) => t(`ReportsPage:financialSummary.${key}`);
  const [dateRange, setDateRange] = useState<DatesRangeValue>(() => getFinancialSummaryMonth(timezone));
  const [appliedDates, setAppliedDates] = useState(() => buildFinancialSummaryDates(dateRange)!);
  const [expensePage, setExpensePage] = useState(1);
  const [expenseCategory, setExpenseCategory] = useState<ExpenseCategory | ''>('');
  const { data, isLoading, isFetching, isError, error, refetch } = useGetFinancialSummary({
    storeId, ...appliedDates
  });
  const apiError = error as { response?: { status?: number; data?: { errors?: string[] } } } | null;
  const deniedByApi = apiError?.response?.status === 403;
  const detailError = apiError?.response?.data?.errors?.find((code) => [
    'backend.errors.financialSummary.invalidPeriod',
    'backend.errors.financialSummary.currencyMismatch',
    'backend.errors.financialSummary.currencyUnavailable'
  ].includes(code));
  const expenses = useGetExpenses(Boolean(data) && !isError, storeId, {
    dateFrom: data?.period.fromDate || appliedDates.fromDate,
    dateTo: data?.period.toDate || appliedDates.toDate,
    category: expenseCategory || undefined,
    page: expensePage - 1,
    limit: EXPENSE_PAGE_SIZE
  });

  const updateRange = (value: DatesRangeValue) => {
    setDateRange(value);
    const dates = buildFinancialSummaryDates(value);
    if (dates) {
      setAppliedDates(dates);
      setExpensePage(1);
    }
  };
  const rangeInvalid = Boolean(dateRange[0] && dateRange[1] && !buildFinancialSummaryDates(dateRange));
  const money = (amount: number) => formatMoneyString(amount, 2, '.', ',', data?.currencySymbol || '$') || '';
  const methodLabel = (method: string) => t(`FieldName:paymentMethods.${method}`, { defaultValue: method });
  const drilldown = (path: string) => data ? buildFinancialDrilldownLink(path, storeId, data.period) : '';
  const globalUnpaidLink = `/reports/unpaid-orders-report?${new URLSearchParams({ storeId, allDates: 'true', timezone })}`;
  const sales = data?.salesFromOrdersCreatedInPeriod ?? NaN;
  const paid = data?.paidForOrdersCreatedInPeriodAsOfNow ?? NaN;
  const unpaid = data?.unpaidForOrdersCreatedInPeriodAsOfNow ?? NaN;
  const splitTotal = paid + unpaid;
  // One cent of reported rounding is allowed. Percentages are only for drawing;
  // the amount cards always display the original API values, including overpayments.
  const canIllustrateCollections = [sales, paid, unpaid].every(Number.isFinite) &&
    sales > 0 && paid >= 0 && unpaid >= 0 && splitTotal > 0 && Math.abs(sales - splitTotal) <= 0.01 + 1e-9;
  const paidShare = canIllustrateCollections ? (paid / splitTotal) * 100 : 0;
  const metric = (id: string, key: keyof FinancialSummaryResponse, help?: string, href?: string, compact = false) =>
    <Metric key={id} id={id}
      label={id === 'result' && data!.estimatedActivityResult < 0 ? text('estimatedLoss') : text(`metrics.${id}`)}
      value={money(data![key] as number)} help={help} href={href} compact={compact} />;

  return <Stack gap="lg">
    <Card withBorder radius="md">
      <Group align="end" justify="space-between" gap="md">
        <DatePickerInput
          type="range" label={text('dateRange')} value={dateRange} onChange={updateRange}
          allowSingleDateInRange clearable={false} valueFormat="DD/MM/YYYY"
        />
        <Button variant="light" onClick={() => updateRange(getFinancialSummaryMonth(timezone))}>
          {text('currentMonth')}
        </Button>
      </Group>
      {rangeInvalid && <Text c="red" size="sm" mt="xs">{text('invalidRange')}</Text>}
      <Text size="sm" c="dimmed" mt="sm">{text('timezone')}: {timezone}</Text>
    </Card>
    {isError ? <Alert color="red" title={text(deniedByApi ? 'noPermission' : 'loadError')}>
      {detailError && <Text>{t(`Backend:${detailError}`)}</Text>}
      {!deniedByApi && <Button variant="light" mt="sm" onClick={() => void refetch()}>{t('CommonButtons:retry')}</Button>}
    </Alert> : isLoading || !data ? <LoaderOverlay visible /> : <>
      <LoaderOverlay visible={Boolean(isFetching)} />
      <Text size="sm" c="dimmed">
        {data.period.fromDate} — {data.period.toDate} · {data.currencyCode} · {text('generatedAt')}: {' '}
        {moment.tz(data.generatedAt, timezone).format('DD/MM/YYYY HH:mm')}
      </Text>
      {data.salesFromOrdersCreatedInPeriod === 0 && data.paymentsReceivedInPeriodGross === 0 &&
        data.registeredExpensesInPeriod === 0 && data.cashMovements.manualInCount === 0 &&
        data.cashMovements.manualOutCount === 0 && data.cashMovements.expenseLinkedOutCount === 0 &&
        <Alert color="blue">{text('emptyPeriod')}</Alert>}

      <section aria-labelledby="financial-summary-activity">
        <Stack gap="sm">
          <Title order={2} id="financial-summary-activity">{text('activityTitle')}</Title>
          <Text size="sm" c="dimmed">{text('activityDescription')}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {metric('sales', 'salesFromOrdersCreatedInPeriod', text('salesNote'), drilldown('/reports/sales-report'))}
            {metric('expenses', 'registeredExpensesInPeriod', text('expensesNote'))}
            {metric('result', 'estimatedActivityResult', text('resultNote'))}
            <Metric id="margin" label={text('margin')} value={data.estimatedActivityMarginPercent === null
              ? '—' : `${data.estimatedActivityMarginPercent.toFixed(2)}%`}
              help={data.estimatedActivityMarginPercent === null ? text('marginUnavailable') : undefined} />
          </SimpleGrid>
          <Alert color="yellow">{text('activityLimitations')}</Alert>
        </Stack>
      </section>

      <section aria-labelledby="financial-summary-balances">
        <Stack gap="sm">
          <Title order={2} id="financial-summary-balances">{text('currentBalancesTitle')}</Title>
          <Text size="sm" c="dimmed">{text('currentBalancesDescription')}</Text>
          <Card withBorder radius="md">
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {metric('cohort-sales', 'salesFromOrdersCreatedInPeriod', undefined, undefined, true)}
              {metric('cohort-paid', 'paidForOrdersCreatedInPeriodAsOfNow', text('cohortPaidNote'), undefined, true)}
              {metric('cohort-outstanding', 'unpaidForOrdersCreatedInPeriodAsOfNow', text('cohortOutstandingNote'), drilldown('/reports/unpaid-orders-report'), true)}
            </SimpleGrid>
            {canIllustrateCollections && <Stack gap="xs" mt="md">
              <div role="img" data-testid="cohort-collection-bar"
                aria-label={t('ReportsPage:financialSummary.collectionBarLabel', { paid: money(paid), unpaid: money(unpaid) })}
                style={{ display: 'flex', height: 10, borderRadius: 100, overflow: 'hidden' }}>
                <span aria-hidden="true" data-paid-segment style={{ width: `${paidShare}%`, background: 'var(--mantine-color-teal-8)' }} />
                <span aria-hidden="true" data-unpaid-segment style={{ width: `${100 - paidShare}%`, background: 'var(--mantine-color-orange-8)' }} />
              </div>
              <Group gap="lg" wrap="wrap">
                <Text size="xs"><span aria-hidden="true" style={{ color: 'var(--mantine-color-teal-8)' }}>● </span>{text('collectedLegend')}</Text>
                <Text size="xs"><span aria-hidden="true" style={{ color: 'var(--mantine-color-orange-8)' }}>● </span>{text('outstandingLegend')}</Text>
              </Group>
            </Stack>}
            {!canIllustrateCollections && sales !== 0 && <Text size="xs" c="dimmed" mt="sm">{text('collectionBarUnavailable')}</Text>}
          </Card>
          <Divider my="xs" />
          {metric('global-outstanding', 'totalOutstandingReceivablesAsOfNow', text('globalOutstandingNote'), globalUnpaidLink)}
          <Text size="sm" c="dimmed">{text('currentBalancesNote')}</Text>
        </Stack>
      </section>

      <section aria-labelledby="financial-summary-receipts">
        <Stack gap="sm">
          <Title order={2} id="financial-summary-receipts">{text('receiptsTitle')}</Title>
          <Text size="sm" c="dimmed">{text('receiptsDescription')}</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            {metric('payments-gross', 'paymentsReceivedInPeriodGross', text('paymentsNote'), drilldown('/reports/payment-lines-report'))}
            {metric('fees', 'paymentsReceivedInPeriodProcessingFees')}
            {metric('payments-net', 'paymentsReceivedInPeriodNet', text('paymentsNetNote'))}
          </SimpleGrid>
        </Stack>
      </section>

      <section aria-labelledby="financial-summary-movements">
        <Stack gap="sm">
          <Title order={2} id="financial-summary-movements">{text('flowTitle')}</Title>
          <Text size="sm" c="dimmed">{text('flowDescription')}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            <Metric id="manual-in" label={text('metrics.manual-in')} value={money(data.cashMovements.manualIn)}
              help={`${text('records')}: ${data.cashMovements.manualInCount}`} />
            <Metric id="manual-out" label={text('metrics.manual-out')} value={money(data.cashMovements.manualOut)}
              help={`${text('records')}: ${data.cashMovements.manualOutCount}`} />
            <Metric id="expense-linked-out" label={text('metrics.expense-linked-out')} value={money(data.cashMovements.expenseLinkedOut)}
              help={`${text('records')}: ${data.cashMovements.expenseLinkedOutCount}`} />
            <Metric id="flow" label={text('metrics.flow')} value={money(data.cashMovements.indicativeRecordedFlow)} />
            <Metric id="unlinked-expenses" label={text('metrics.unlinked-expenses')} value={money(data.cashMovements.registeredExpensesWithoutMovement)} help={text('unlinkedExpensesNote')} />
          </SimpleGrid>
          <Text size="sm">{text('flowFormula')}</Text>
          <ReportLink href={drilldown('/reports/cashierbox-movements-report')}>{text('viewMovements')}</ReportLink>
        </Stack>
      </section>

      <Card withBorder radius="md">
        <Title order={3} mb="md">{text('paymentMethods')}</Title>
        <Table.ScrollContainer minWidth={540}><Table striped>
          <Table.Thead><Table.Tr>
            <Table.Th>{text('paymentMethod')}</Table.Th><Table.Th>{text('paymentCount')}</Table.Th>
            <Table.Th>{text('gross')}</Table.Th><Table.Th>{text('fees')}</Table.Th><Table.Th>{text('net')}</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>{data.paymentsByMethod.map((row) => <Table.Tr key={row.paymentMethod}>
            <Table.Td>{methodLabel(row.paymentMethod)}</Table.Td><Table.Td>{row.count}</Table.Td>
            <Table.Td>{money(row.gross)}</Table.Td><Table.Td>{money(row.processingFees)}</Table.Td><Table.Td>{money(row.net)}</Table.Td>
          </Table.Tr>)}</Table.Tbody>
        </Table></Table.ScrollContainer>
        {data.paymentsByMethod.length === 0 && <Text c="dimmed">{text('noRecords')}</Text>}
      </Card>

      <Card withBorder radius="md">
        <Title order={3} mb="md">{text('movementMethods')}</Title>
        <Table.ScrollContainer minWidth={640}><Table striped>
          <Table.Thead><Table.Tr>
            <Table.Th>{text('paymentMethod')}</Table.Th><Table.Th>{text('metrics.manual-in')}</Table.Th>
            <Table.Th>{text('metrics.manual-out')}</Table.Th><Table.Th>{text('metrics.expense-linked-out')}</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>{data.cashMovements.byPaymentMethod.map((row) => <Table.Tr key={row.paymentMethod}>
            <Table.Td>{methodLabel(row.paymentMethod)}</Table.Td><Table.Td>{money(row.manualIn)}</Table.Td>
            <Table.Td>{money(row.manualOut)}</Table.Td><Table.Td>{money(row.expenseLinkedOut)}</Table.Td>
          </Table.Tr>)}</Table.Tbody>
        </Table></Table.ScrollContainer>
        {data.cashMovements.byPaymentMethod.length === 0 && <Text c="dimmed">{text('noRecords')}</Text>}
      </Card>

      <Card withBorder radius="md">
        <Title order={3} mb="sm">{text('timelineTitle')}</Title>
        <Text size="sm" c="dimmed" mb="md">{text('timelineNote')}</Text>
        <LineChart h={300} data={data.timeline} dataKey="date" curveType="linear" withDots={false}
          valueFormatter={money}
          series={[
            { name: 'sales', label: text('metrics.sales'), color: 'blue.6' },
            { name: 'paymentsReceived', label: text('metrics.payments-gross'), color: 'teal.6' },
            { name: 'expenses', label: text('metrics.expenses'), color: 'orange.6' },
            { name: 'estimatedResult', label: text('timelineResult'), color: 'violet.6' }
          ]} />
      </Card>

      <Card withBorder radius="md">
        <Title order={3} mb="md">{text('expenseCategories')}</Title>
        <Text size="sm" c="dimmed" mb="md">{text('records')}: {data.expensesCount}</Text>
        <Table.ScrollContainer minWidth={440}><Table striped>
          <Table.Thead><Table.Tr><Table.Th>{text('expenseCategory')}</Table.Th>
            <Table.Th>{text('amount')}</Table.Th><Table.Th>{text('records')}</Table.Th><Table.Th>{text('share')}</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>{data.expensesByCategory.map((row) => <Table.Tr key={row.category}>
            <Table.Td>{t(`ExpensesPage:categories.${row.category}`)}</Table.Td><Table.Td>{money(row.amount)}</Table.Td>
            <Table.Td>{row.count}</Table.Td><Table.Td>{row.percent.toFixed(2)}%</Table.Td>
          </Table.Tr>)}</Table.Tbody>
        </Table></Table.ScrollContainer>
        {data.expensesByCategory.length === 0 && <Text c="dimmed">{text('noRecords')}</Text>}
      </Card>

      <Card withBorder radius="md">
        <Title order={3} mb="sm">{text('expenseDetail')}</Title>
        <Text size="sm" c="dimmed" mb="md">{text('expenseDetailNote')}</Text>
        <Select clearable mb="md" label={text('expenseCategory')} value={expenseCategory || null}
          data={data.expensesByCategory.map(({ category }) => ({ value: category, label: t(`ExpensesPage:categories.${category}`) }))}
          onChange={(value) => { setExpenseCategory((value || '') as ExpenseCategory | ''); setExpensePage(1); }} />
        {expenses.isError ? <Alert color="red" title={text('expenseLoadError')}>
          <Button variant="light" onClick={() => void expenses.refetch()}>{t('CommonButtons:retry')}</Button>
        </Alert> : expenses.isLoading || expenses.isFetching ? <LoaderOverlay visible /> : <>
          <Table.ScrollContainer minWidth={620}><Table striped>
            <Table.Thead><Table.Tr><Table.Th>{t('ExpensesPage:date')}</Table.Th><Table.Th>{t('ExpensesPage:description')}</Table.Th>
              <Table.Th>{text('expenseCategory')}</Table.Th><Table.Th>{text('paymentMethod')}</Table.Th><Table.Th>{text('amount')}</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>{expenses.data?.expenses.map((expense) => <Table.Tr key={expense._id}>
              <Table.Td>{moment.tz(expense.expenseDate, timezone).format('DD/MM/YYYY')}</Table.Td>
              <Table.Td><ReportLink href={`/expenses/${expense._id}?${new URLSearchParams({ storeId })}`}>{expense.description}</ReportLink></Table.Td>
              <Table.Td>{t(`ExpensesPage:categories.${expense.category}`)}</Table.Td><Table.Td>{methodLabel(expense.paymentMethod)}</Table.Td>
              <Table.Td>{money(expense.amount)}</Table.Td>
            </Table.Tr>)}</Table.Tbody>
          </Table></Table.ScrollContainer>
          {expenses.data?.expenses.length === 0 && <Text c="dimmed">{text('noRecords')}</Text>}
          <Group justify="space-between" mt="md">
            <Text size="sm">{text('records')}: {expenses.data?.totalRows || 0}</Text>
            <Pagination value={expensePage} onChange={setExpensePage}
              total={Math.max(1, Math.ceil((expenses.data?.totalRows || 0) / EXPENSE_PAGE_SIZE))} />
          </Group>
        </>}
      </Card>
      <Card withBorder radius="md">
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{text('calculationsTitle')}</summary>
          <Stack gap="sm" mt="md">
            <Text size="sm">{text('calculationDates')}</Text>
            <Text size="sm">{text('calculationExample')}</Text>
            <Text size="sm">{text('calculationResult')}</Text>
            <Text size="sm">{text('calculationNet')}</Text>
            <Text size="sm">{text('manualMovementsNote')}</Text>
            <Text size="sm">{text('noDoubleCountNote')}</Text>
            <Text size="sm">{text('legacyDifferences')}</Text>
            <Text size="sm">{text('calculationLimitations')}</Text>
          </Stack>
        </details>
      </Card>
    </>}
  </Stack>;
}

export default function FinancialSummaryReport() {
  const { t } = useTranslation(['ReportsPage', 'CommonButtons']);
  const { user } = useSelector((state: { user: { user: unknown } }) => state.user);
  const location = useLocation();
  const storeId = new URLSearchParams(location.search).get('storeId') || '';
  const canView = canViewFinancialSummary(user);
  const validStoreId = Boolean(storeId) && storeId !== 'all';
  const storeQuery = useGetStoreById(canView && validStoreId, storeId);
  const timezone = storeQuery.data?.timezone;

  return <CatalogContainer pos="relative">
    <CatalogListHeader title={t('ReportsPage:financialSummary.title')} showCreateButton={false} />
    <Text size="sm" c="dimmed" mb="lg">{t('ReportsPage:financialSummary.description')}</Text>
    {!canView ? <Alert color="red">{t('ReportsPage:financialSummary.noPermission')}</Alert>
      : !validStoreId ? <Alert>{t('ReportsPage:financialSummary.selectStore')}</Alert>
      : storeQuery.isLoading ? <LoaderOverlay visible />
      : storeQuery.isError ? <Alert color="red" title={t('ReportsPage:financialSummary.storeLoadError')}>
        <Button variant="light" onClick={() => void storeQuery.refetch()}>{t('CommonButtons:retry')}</Button>
      </Alert>
      : !timezone || !moment.tz.zone(timezone) ? <Alert color="red">{t('ReportsPage:financialSummary.invalidTimezone')}</Alert>
      : <FinancialSummaryContent key={`${storeId}:${timezone}`} storeId={storeId} timezone={timezone} />}
  </CatalogContainer>;
}
